// JobHeader (job page): the top of a job. Where it was opened from, as a crumb (and Copy link); the
// one main button, which follows the job (Send the confirmation, Complete the job, Record a payment),
// the Job actions menu and × at the end; the job's name; who it's for and where; then one sentence
// saying where it stands, drawn with a StateMark the way the board draws a visit, and the StoryStrip
// under it. No chips: the state is the one word that matters, the filing facts live in Details.
// Round 2 (Amir 26 Oct points 4–5): a muted kicker over the title (job type · job number), and in place
// of the standing sentence a state slot (a StateLine: where it stands, and the next thing), with no
// StateMark at the top of the page. The crumb is then only where it was opened from.
// Round 3 (Amir 26 Oct: "sharpen it"): the type and number move to one quiet line under who and where
// (meta), and the state slot carries only a word the strip can't say (Paid, On hold, money owing).
// Reads only --jobhead-*; Button, Menu, Close, Link and StateMark read their own.
import type { ReactNode } from 'react';
import { StateMark, type MarkState } from '../../components/StateMark/StateMark.tsx';
import './JobHeader.css';

export interface JobHeaderProps {
  /** Where it was opened from, and Copy link: Links. */
  crumb: ReactNode;
  /** The title; while loading, a Skeleton bar in its place (a SkeletonGroup names it). */
  title: ReactNode;
  /** Round 2: over the title, muted: "Sold work install · Job 24612". */
  kicker?: ReactNode;
  /** Round 2: where it stands and what's next (a StateLine), in place of standing. */
  state?: ReactNode;
  /** Round 3: one quiet line under who and where: "Sold work install · Job 24612". */
  meta?: ReactNode;
  /** Who it's for (a Link to their record) and where. */
  who: ReactNode;
  /** Where it stands; none before the job has loaded. */
  standing?: { mark: MarkState; word: string; line: string; end?: ReactNode } | undefined;
  /** The one main button, if the viewer has a next step. */
  main?: ReactNode;
  /** Leave the main button out on a phone, where it would repeat the crumb. */
  mainOffOnPhone?: boolean;
  /** The Job actions MenuButton. */
  actions?: ReactNode;
  /** ×: a Close placed inline, last in the row (the one close control, round 2). */
  close: ReactNode;
  /** The StoryStrip. */
  strip?: ReactNode;
  /** The title's id, for the page's aria-labelledby. */
  titleId?: string;
}

export function JobHeader({
  crumb,
  title,
  kicker,
  state,
  meta,
  who,
  standing,
  main,
  mainOffOnPhone = false,
  actions,
  close,
  strip,
  titleId,
}: JobHeaderProps) {
  return (
    <header className="fl-jobhead">
      <nav className="fl-jobhead__crumb" aria-label="Where you came from">
        {crumb}
      </nav>
      <div className="fl-jobhead__acts">
        {main ? (
          <span className="fl-jobhead__main" data-phone={mainOffOnPhone ? 'off' : undefined}>
            {main}
          </span>
        ) : null}
        {actions}
      </div>
      {close ? <span className="fl-jobhead__close">{close}</span> : null}
      {kicker ? <p className="fl-jobhead__kicker">{kicker}</p> : null}
      <h1 id={titleId} className="fl-jobhead__title" data-kicked={kicker ? '' : undefined}>
        {title}
      </h1>
      <p className="fl-jobhead__who">{who}</p>
      {meta ? <p className="fl-jobhead__meta">{meta}</p> : null}
      {standing ? (
        <p className="fl-jobhead__stand">
          <StateMark state={standing.mark} />
          <span>
            <b className="fl-jobhead__word">{standing.word}</b>
            <span className="fl-jobhead__sep" aria-hidden="true">
              ·
            </span>
            {standing.line}
            {standing.end ? (
              <>
                <span className="fl-jobhead__sep" aria-hidden="true">
                  ·
                </span>
                {standing.end}
              </>
            ) : null}
          </span>
        </p>
      ) : null}
      {state ? <div className="fl-jobhead__state">{state}</div> : null}
      {strip ? <div className="fl-jobhead__strip">{strip}</div> : null}
    </header>
  );
}

/** The dot between two things in a line ("Jordan Harper · 1184 Larch Way"). */
export function Sep() {
  return (
    <span className="fl-jobhead__sep" aria-hidden="true">
      ·
    </span>
  );
}
